import React, { useState, useEffect, useRef } from "react";
import { useLanguage } from "../hooks/useLanguage";
import { useLocation, useNavigate } from "react-router";
import { useOnboardingData } from "../context/OnboardingDataContext";
import { trackGTMEvent } from "../utils/gtm";
import { useAuth } from "../context/authContext";
import { deleteCookie } from "../utils/deleteCookie";
import { API_METHODS, ONBOARDING_SESSION_KEY } from "../../constants";
import useApi from "../hooks/useApi";
import { useErrorModal } from "../context/ErrorModalContext";
import { FLOW_TYPES } from "../flows";

const SimRegistrationPage = () => {
  const { t, currentLanguage, changeLanguage, languages } = useLanguage();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [sessionCleared, setSessionCleared] = useState(false);
  const dropdownRef = useRef(null);
  const { showError } = useErrorModal();
  const { clearToken } = useAuth();
  const { clearOnboardingData, updateOnboardingData, onboardingData } =
    useOnboardingData();
  const [sessionInfoResponse, sessionInfoRequest] = useApi(
    `/session-info`,
    null,
    API_METHODS.GET
  );
  const [featureFlagsResponse, featureFlagsRequest] = useApi(
    `/feature-flags`,
    null,
    API_METHODS.GET
  );
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const navigateToStep = (current_step) => {
    updateOnboardingData({ redirectedFromSession: true });
    if (current_step == "OTP_VERIFIED" || current_step == "PAYLOAD_DECRYPTED") {
      return navigate("/digital-onboarding/step-one");
    } else if (
      current_step == "REGISTRATION_STATUS_CHECKED" ||
      current_step == "REG_TYPE_LIST_RETRIEVED"
    ) {
      return navigate("/digital-onboarding/step-two");
    } else if (
      current_step == "REG_TYPE_SUBMITTED" ||
      current_step == "DOCUMENT_SCANNED"
    ) {
      return navigate("/digital-onboarding/step-three");
    } else if (
      current_step == "SELFIE_CAPTURED" ||
      current_step == "ADDRESS_DATA_RETRIEVED" ||
      current_step == "USER_DETAILS_SUBMITTED" ||
      current_step == "COMPANY_DETAILS_SUBMITTED" ||
      current_step == "ADDITIONAL_DOCS_TYPES_RETRIEVED" ||
      current_step == "SUMMARY_RETRIEVED"
    ) {
      return navigate("/digital-onboarding/step-four");
    } else if (
      current_step == "DOCUMENTS_UPLOADED" ||
      current_step == "EDIT_RETRIEVED"
    ) {
      return navigate("/digital-onboarding/step-five");
    } else {
      return navigate("/digital-onboarding");
    }
  };

  useEffect(() => {
    if (sessionInfoResponse.isError && sessionInfoResponse.error) {
      showError({
        message: sessionInfoResponse.error.displayMessage,
        buttons: [
          {
            label: t("verification.btnExitLabel"),
            variant: "secondary",
            onClick: () => {
              navigate("/digital-onboarding");
            },
          },
        ],
      });
    } else if (sessionInfoResponse.data && !sessionInfoResponse.isLoading) {
      if (
        sessionInfoResponse.data?.sessionValid == true &&
        sessionInfoResponse.data?.channelId != "C01"
      ) {
        updateOnboardingData(sessionInfoResponse.data);
        navigateToStep(sessionInfoResponse.data.currentStep);
      } else {
        if (onboardingData?.encryptedPayload) {
          return navigate("/digital-onboarding/step-one");
        }
        clearOnboardingData();
        clearToken();
        deleteCookie("auth_token");
        sessionStorage.removeItem(ONBOARDING_SESSION_KEY);
        navigate("/digital-onboarding/step-one");
      }
    }
  }, [sessionInfoResponse]);

  useEffect(() => {
    trackGTMEvent({
      event_category: "",
      event_action: "",
      event_label: "",
      event_number: "Event 0.3",
      event: "page_view",
      scr_user: "2.0",
    });

    // Reaching home ends the previous journey, so its X-Session-ID must not
    // survive into the next validateAccount. The one exception is the hop from
    // /decrypt-payload, which flags itself — that session is still in use.
    if (!location.state?.fromPayload) {
      clearToken();
      sessionStorage.removeItem(ONBOARDING_SESSION_KEY);
    }
    setSessionCleared(true);
  }, []);

  // Separate effect on purpose: clearToken() above only schedules a state
  // update, so a request fired in the same tick would still carry the old
  // X-Session-ID and get the token re-armed from the echoed response header.
  useEffect(() => {
    if (sessionCleared && !onboardingData?.encryptedPayload) {
      featureFlagsRequest();
    }
  }, [sessionCleared]);

  const handleContinue = () => {
    trackGTMEvent({
      event_category: "SCR-Engagement",
      event_action: "Click",
      event_label: "Continue",
      tap_text: "Continue",
      entry_point: "GLOBE",
      sub_step_name: "Landing",
      environment: "Beta",
      client_id: "",
      step_name: "Landing",
      event_number: "Event 0.1",
      event: "scr_cta",
    });
    sessionInfoRequest();
  };

  const handleEditSCR = () => {
    updateOnboardingData({ activeFlow: FLOW_TYPES.UPDATE_SCR });
    navigate("/digital-onboarding/update-scr/welcome");
  };

  const handleTransferOwnership = () => {
    updateOnboardingData({ activeFlow: FLOW_TYPES.CHANGE_OF_OWNERSHIP });
    navigate("/digital-onboarding/chow/welcome");
  };

  const handleLearnMore = () => {
    trackGTMEvent({
      event_category: "SCR-Engagement",
      event_action: "Click",
      event_label: "Landing-Learn more about Sim Registartion",
      tap_text: "Learn more about SIM Registration",
      entry_point: "GLOBE",
      sub_step_name: "Landing",
      environment: "Beta",
      client_id: "",
      step_name: "Landing",
      event_number: "Event 0.2",
      event: "scr_cta",
    });
    window.open("https://www.globe.com.ph/register-sim-card#faqs", "_blank");
  };

  return (
    <div className="sim-registration-container">
      {/* Main Content */}
      <div className="main-content">
        {/* SIM Registration Icon */}
        <div style={{ marginBottom: "18px" }}>
          <div className="sim-icon-container">
            {/* SIM Card Icon */}
            <img src="/assets/sim_icon.svg" alt="SIM Icon" />
          </div>

          <h1 className="main-title">{t("landing.title")}</h1>
        </div>

        {/* Language Switcher */}
        <div style={{ marginBottom: "11px" }}>
          <p className="language-label">{t("landing.chooseLanguage")}</p>
          <div className="dropdown-container" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="dropdown-button"
            >
              {languages.find((lang) => lang.key === currentLanguage)?.label ||
                currentLanguage}
              <span
                className={`dropdown-arrow ${
                  dropdownOpen ? "dropdown-arrow-open" : ""
                }`}
                style={{ width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
              >
                <img src="/assets/arrow.svg" alt="" style={{ width: 12, height: 6 }} />
              </span>
            </button>

            {dropdownOpen && (
              <div className="dropdown-menu">
                {languages.map((lang) => (
                  <button
                    key={lang.key}
                    onClick={() => {
                      changeLanguage(lang.key);
                      setDropdownOpen(false);
                    }}
                    className="dropdown-item"
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Learn More Link */}
        <div style={{ textAlign: "center", marginBottom: "31px" }}>
          <span
            onClick={handleLearnMore}
            className="learn-more-link"
          >
            {t("landing.buttons.learnMore")}
          </span>
        </div>

        {/* Steps List */}
        <div className="steps-container" style={{ marginTop: 0, marginBottom: "34px", paddingLeft: "clamp(12px, 4vw, 26px)" }}>
          <div className="steps-list" style={{ gap: "15.2px", lineHeight: "21px" }}>
            <div className="step-item">
              <span className="step-number">1.</span>
              <span>{t("landing.steps.step1")}</span>
            </div>
            <div className="step-item">
              <span className="step-number">2.</span>
              <span>{t("landing.steps.step2")}</span>
            </div>
            <div className="step-item">
              <span className="step-number">3.</span>
              <span>{t("landing.steps.step3")}</span>
            </div>
            <div className="step-item">
              <span className="step-number">4.</span>
              <span>{t("landing.steps.step4")}</span>
            </div>
            <div className="step-item">
              <span className="step-number">5.</span>
              <span>{t("landing.steps.step5")}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="buttons-container" style={{ gap: "10px" }}>
          <button onClick={handleContinue} className="primary-button">
            {t("landing.buttons.registerSIM")}
          </button>

          {!onboardingData?.encryptedPayload &&
            featureFlagsResponse.data &&
            featureFlagsResponse.data?.updateAllowed !== false && (
            <>
              <button onClick={handleEditSCR} className="secondary-button">
                {t("landing.buttons.editSCR")}
              </button>

              <button onClick={handleTransferOwnership} className="tertiary-button">
                {t("landing.buttons.transferOwnership")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SimRegistrationPage;
